<template>
  <v-col
    class="d-flex flex-column"
    cols="12"
  >
    <confirmation-dialog
      v-if="confirmDelete"
      visible
      :title="$t('title.delete_translation')"
      :message="$t('message.confirm_translaton_delete', [enabledLocales.find((l) => l.locale === model)?.language])"
      @confirm="emit('delete', model)"
      @close="confirmDelete = false"
    />
    <p :class="['subheading mb-0 text-left']">
      {{ $t('label.language') }}
    </p>
    <div class="d-flex flex-row">
      <div class="flex overflow-hidden">
        <v-tabs
          v-model="model"
          hide-slider
          class="pb-0 mlg-picker-tabs flex-shrink"
          show-arrows
          @change="emit('input', $event)"
        >
          <v-tab
            v-for="l in translatedLocales.map((l) => props.enabledLocales.find((el) => el.locale === l))"
            :key="l?.id"
            :class="[{ 'pr-1': localeCanBeDeleted(l), 'error-text': l?.locale && errorLocales?.includes(l.locale) }, 'text-center']"
            :tab-value="l?.locale"
          >
            <span class="error-text">{{ l?.locale }}</span>
            <template v-if="localeCanBeDeleted(l)">
              <v-spacer />
              <v-btn
                small
                icon
                class="mr-0 pr-0"
                @click.stop="confirmDelete = true"
              >
                <v-icon small>
                  mdi-close
                </v-icon>
              </v-btn>
            </template>
          </v-tab>
        </v-tabs>
      </div>
      <div
        v-if="availableLocales.length > 0"
        class="flex flex-row flex-grow-0"
      >
        <v-menu
          fixed
          content-class="mlg-menu"
          style="position: fixed"
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-btn
              style="margin-top: 10px"
              outlined
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>

          <v-list class="py-0">
            <v-subheader>
              {{ $t('label.add_translation') }}
            </v-subheader>
            <v-list-item
              v-for="l in availableLocales"
              :key="l.id"
              @click="emit('add', l.locale)"
            >
              <v-list-item-title>{{ l.language }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    <v-row class="mlg-picker-frame ma-0 pa-2">
      <slot />
    </v-row>
  </v-col>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import Locale from '@/model/Locale';
import ConfirmationDialog from './ConfirmationDialog.vue';

const emit = defineEmits<
{(e: 'input', event: string): void,
  (e: 'delete', event: string): void,
  (e: 'add', event: string): void
}
>();

const props = defineProps<
{
  enabledLocales: Locale[],
  translatedLocales: string[],
  errorLocales?: string[],
  value: string,
  enableRemoval?: boolean
}
>();

const model = ref(props.value as string);
watch(() => props.value, () => { model.value = props.value; });

const availableLocales = computed(
  () => props.enabledLocales.filter((l) => !props.translatedLocales.includes(l.locale)),
);

const localeCanBeDeleted = (
  l?: Locale,
) => l && l?.locale === model.value && props.translatedLocales.length > 1 && props.enableRemoval;

const confirmDelete = ref(false);

</script>

<style scoped lang="scss">
.mlg-picker-frame {
  border-radius: 0px 4px 4px 4px;
  border: 1px solid $blue-color;
}

.mlg-picker-tabs ::v-deep(.v-tab) {
  border: solid rgba(255, 255, 255, 0.3);
  margin-top: 8px;
  border-width: 1px 0px 0px 1px;
  transition: all;
}

.mlg-picker-tabs ::v-deep(.v-tab.v-tab--active + .v-tab) {
  border-left-width: 0;
}

.mlg-picker-tabs ::v-deep(.v-tab):first-child {
  border-radius: 4px 0px 0px 0px;
  border-left-width: 1px;
  border-right-width: 0px;
}

.mlg-picker-tabs ::v-deep(.v-tab):last-child {
  border-radius: 0px 4px 0px 0px;
  border-left-width: 1px;
  border-right-width: 1px;
}

.mlg-picker-tabs ::v-deep(.v-tab.v-tab--active) {
  border: solid #3355ff;
  border-width: 1px 1px 0px 1px;
  margin-top: 4px;
  border-radius: 4px 4px 0px 0px;
}

.mlg-picker-tabs ::v-deep(.v-tab.error-text) {
  border: solid #FF002B;
  color: #FF002B !important;
  border-width: 1px 1px 0px 1px;
  margin-right: -1px;
}

.mlg-menu {
  position: fixed !important;
  max-height: 90%;
}
</style>
